import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { PromoIcon } from 'components/no-auth/common/promo-icon/PromoIcon';
import { PromoText } from 'components/no-auth/common/promo-text/PromoText';

import { HELP_PAYSEND } from 'constants/UrlConstants';

import { getLangCountryPrefix } from 'helpers/utils';

import * as Sentry from '@sentry/react';
import { useHandleCloseDropdownMenu } from 'hooks/useHandleCloseDropdownMenu';

import { SearchDropdown, SearchDropdownItem, SearchWrapper } from './styles';
import { SearchIconWrap } from '../../form/components/InputSearch';
import { PromoRow } from '../common/PromoRow/PromoRow';
import PromoInputWrapper from '../form/PromoInputWrapper';
import { getZendeskLocale } from '../utils';

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value]);

    return debouncedValue;
};

const searchArticles = async (lang, country, query) => {
    const href = `${HELP_PAYSEND}/api/v2/help_center/articles/search.json?query=${query}&locale=${getZendeskLocale(lang, country)}`;
    try {
        const resp = await fetch(href, { method: 'GET' });
        if (resp.status === 200) {
            const json = await resp.json();
            return json;
        }
    } catch (e) {
        Sentry.captureException(e, {
            level: 'log',
        });
        return null;
    }
};

const getArticles = async (lang, country, query, sections) => {
    const articles = await searchArticles(lang, country, query);

    if (articles) {
        return articles.results.map((article) => {
            const section = sections.find((s) => s.id === article.section_id);
            return {
                id: article.id,
                title: article.title,
                section_id: article.section_id,
                category_id: section.category_id,
                directLink: article.label_names?.includes('directLink'),
            };
        });
    }

    return null;
};

const ZendeskHelpSearch = (props) => {
    const wrapperRef = useRef(null);

    const [search, setSearch] = useState('');
    const [foundArticles, setFoundArticles] = useState([]);

    const [isOpenDropdownMenu, setOpenDropdownMenu] = useHandleCloseDropdownMenu(wrapperRef);

    const url = props.isHelpMobile
        ? `${getLangCountryPrefix(props.lang, props.country)}/help-mobile`
        : `${getLangCountryPrefix(props.lang, props.country)}/help`;

    const focus = (e) => {
        if (e.currentTarget) {
            const input = e.currentTarget.firstElementChild;
            input.focus();
        } else {
            console.log('Focus method not support');
        }
    };

    const debouncedSearch = useDebounce(search, 1000);

    useEffect(() => {
        const fetchData = async (query) => {
            if (!query) {
                return;
            }

            let articles = await getArticles(props.lang, props.country, query, props.sections);
            articles = articles?.filter((article) => !article.directLink);

            if (articles) {
                setFoundArticles(articles);
                setOpenDropdownMenu(true);
            }
        };

        fetchData(debouncedSearch);
    }, [debouncedSearch]);

    const articlesNames = foundArticles?.map((article) => {
        return (
            <SearchDropdownItem key={article.id} href={`${url}/category/${article.category_id}/section/${article.section_id}/article/${article.id}`}>
                {article.title}
            </SearchDropdownItem>
        );
    });

    return (
        <PromoRow indent="adaptive-32-48-64">
            <SearchWrapper ref={wrapperRef}>
                <PromoInputWrapper
                    name="search"
                    setFocus={focus}
                    leftInputComponent={
                        <SearchIconWrap>
                            <PromoIcon name="search" color="gray80" size={'24px'} />
                        </SearchIconWrap>
                    }
                >
                    <input type="text" value={search} placeholder={props.searchPlaceholder} onChange={(e) => setSearch(e.target.value)} />
                </PromoInputWrapper>
                {isOpenDropdownMenu && (
                    <>
                        {articlesNames?.length === 0 && search && (
                            <SearchDropdown centerAligned>
                                <PromoText tag={'div'} textAlign={'center'} color={'gray40'}>
                                    {props.searchNotFound}
                                </PromoText>
                            </SearchDropdown>
                        )}
                        {articlesNames?.length > 0 && search && <SearchDropdown>{articlesNames}</SearchDropdown>}
                    </>
                )}
            </SearchWrapper>
        </PromoRow>
    );
};

const mapStateToProps = ({ extra }) => {
    return {
        sections: extra?.zendeskHelpData?.sections,
        searchPlaceholder: extra?.strapiApi?.search_placeholder,
        searchNotFound: extra?.strapiApi?.search_not_found,
        lang: extra.noAuthFooter?.language?.lang,
        country: extra.noAuthFooter?.language?.country,
    };
};

export default connect(mapStateToProps, null)(ZendeskHelpSearch);
